import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { IMobileCell, IMobileRow, PrepareRow, RowClickCallback } from './types';

interface IMobileCardListProps {
    rows: IMobileRow[];
    prepareRow: PrepareRow;
    emptyMessage?: ReactNode | string;
    rowClickCallback?: RowClickCallback;
    idPrefix: string;
}

const MobileCardList = ({ rows, prepareRow, emptyMessage, rowClickCallback, idPrefix }: IMobileCardListProps) => {
    const intl = useIntl();

    if (!rows?.length) {
        return (
            <div className="gi-mobile-card__empty" data-testid={`${idPrefix}-cards-empty`}>
                {emptyMessage}
            </div>
        );
    }

    const isClickable = Boolean(rowClickCallback);

    return (
        <ul className="gi-mobile-card-list" data-testid={`${idPrefix}-cards`}>
            {rows.map((row: IMobileRow, rowIndex: number) => {
                prepareRow(row);

                const handleActivate = () => rowClickCallback?.(row.original);
                const rowKey = row.id ?? rowIndex;
                const labelId = `${idPrefix}-card-${rowKey}-label`;
                const hasTitle = row.cells.some(
                    (cell: IMobileCell) => cell.column.isMobileCardTitle && !cell.column.hideOnMobileCard,
                );

                return (
                    <li key={row.id ?? rowIndex} className="gi-mobile-card-list__item">
                        <div
                            id={row.id}
                            className={`gi-mobile-card${isClickable ? ' gi-mobile-card--clickable' : ''}`}
                            onClick={isClickable ? handleActivate : undefined}
                            onKeyDown={
                                isClickable
                                    ? (event: React.KeyboardEvent<HTMLDivElement>) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            handleActivate();
                                        }
                                    }
                                    : undefined
                            }
                            role={isClickable ? 'button' : undefined}
                            tabIndex={isClickable ? 0 : undefined}
                            aria-labelledby={isClickable && hasTitle ? labelId : undefined}
                            data-testid={`${idPrefix}-card-${rowKey}`}
                        >
                            {row.cells.map((cell: IMobileCell, cellIndex: number) => {
                                const column = cell.column;

                                if (column.hideOnMobileCard) {
                                    return null;
                                }

                                const rawLabel = column.mobileCardLabel ?? column.Header;
                                const isTitle = Boolean(column.isMobileCardTitle);
                                const value = cell.render('Cell');

                                if (isTitle) {
                                    return (
                                        <div
                                            key={cellIndex}
                                            id={labelId}
                                            className="gi-mobile-card__title"
                                            data-testid={`${idPrefix}-card-${rowKey}-title`}
                                        >
                                            {value}
                                        </div>
                                    );
                                }

                                if (typeof rawLabel !== 'string') {
                                    return (
                                        <div
                                            key={cellIndex}
                                            className="gi-mobile-card__value-row"
                                            data-testid={`${idPrefix}-card-${rowKey}-cell-${cellIndex}`}
                                        >
                                            {value}
                                        </div>
                                    );
                                }

                                return (
                                    <div
                                        key={cellIndex}
                                        className="gi-mobile-card__row"
                                        data-testid={`${idPrefix}-card-${rowKey}-cell-${cellIndex}`}
                                    >
                                        <div className="gi-mobile-card__label">
                                            {intl.formatMessage({ id: rawLabel })}
                                        </div>
                                        <div className="gi-mobile-card__value">{value}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

export default MobileCardList;
